import { useState } from "react";
import { useLocation } from "wouter";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useAuth } from "@/hooks/useAuth";
import { resetAnalyticsUser } from "@/lib/analytics";
import { logoutAndRedirect } from "@/lib/logout";
import { 
  LayoutDashboard, 
  Building2, 
  Truck, 
  FileText, 
  FolderOpen,
  Phone,
  Users,
  Settings,
  DollarSign,
  Clipboard,
  List,
  Activity,
  LogOut,
  X,
  AlertTriangle,
  Eye,
  Receipt,
  ChevronDown,
  Shield,
  CreditCard,
  Layers,
  Briefcase,
  UserCircle,
  Award,
  BarChart3,
  Bot,
} from "lucide-react";

// Clients section - Companies, Carriers, Contacts
const clientsNavigation = [
  { name: "Companies", href: "/companies", icon: Building2 },
  { name: "Carriers", href: "/carriers", icon: Truck },
  { name: "Contacts", href: "/contacts", icon: Phone },
];

// Getting Work Done section - Dashboard, Work Orders, Authority Letters, Watchlists, FMCSA Incidents, Files
const workNavigation = [
  { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
  { name: "Work Orders", href: "/work-orders", icon: FileText },
  { name: "Authority Letters", href: "/authority-letters", icon: FileText },
  { name: "Watchlists", href: "/watchlists", icon: Eye },
  { name: "FMCSA Incidents", href: "/fmcsa-incidents", icon: AlertTriangle },
  { name: "Files", href: "/documents", icon: FolderOpen },
];

const invoicingNavigation = [
  { name: "Invoices", href: "/invoices", icon: FileText },
  { name: "Payments", href: "/payments", icon: CreditCard },
  { name: "Batch Invoicing", href: "/batch-invoices", icon: Layers },
];

const adminNavigation = [
  { name: "Reports", href: "/reports", icon: BarChart3 },
  { name: "Users", href: "/users", icon: Users },
  { name: "Services", href: "/service-types", icon: Settings },
  { name: "Credential Types", href: "/credential-types", icon: Award },
  { name: "Service Rates", href: "/service-rates", icon: DollarSign },
  { name: "Pass-Through Fees", href: "/pass-through-fees", icon: Receipt },
  { name: "Service Task Lists", href: "/service-templates", icon: List },
  { name: "Task Templates", href: "/task-templates", icon: Clipboard },
  { name: "Audit Trail", href: "/audit-trail", icon: Activity },
  { name: "Settings", href: "/settings", icon: Shield },
  { name: "Connected AI assistants", href: "/connected-apps", icon: Bot },
];

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export default function Sidebar({ isOpen = true, onClose }: SidebarProps) {
  const [location, setLocation] = useLocation();
  const { user } = useAuth();
  const [clientsOpen, setClientsOpen] = useState(true);
  const [workOpen, setWorkOpen] = useState(true);
  const [invoicingOpen, setInvoicingOpen] = useState(true);
  const [adminOpen, setAdminOpen] = useState(true);

  const handleLogout = async () => {
    resetAnalyticsUser();
    await logoutAndRedirect("/");
  };

  const getUserInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return "U";
  };

  const formatRole = (role: string) => {
    return role.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };

  const isClientsRoute = clientsNavigation.some(item => location === item.href);
  const isWorkRoute = workNavigation.some(item => location === item.href);
  const isInvoicingRoute = invoicingNavigation.some(item => location === item.href);
  const isAdminRoute = adminNavigation.some(item => location === item.href);

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <div className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transition-transform duration-300 ease-in-out",
        !isOpen && "md:translate-x-0 -translate-x-full"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo & Company Name */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h1 className="text-xl font-bold text-gray-900">DNA Authority</h1>
            {onClose && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="md:hidden p-1"
              >
                <X className="w-5 h-5" />
              </Button>
            )}
          </div>
        
          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-2 overflow-y-auto">
            {/* Clients Section - Collapsible */}
            <Collapsible open={clientsOpen} onOpenChange={setClientsOpen}>
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  className={cn(
                    "w-full justify-between px-4 py-2 h-auto",
                    isClientsRoute
                      ? "text-primary font-medium"
                      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  )}
                  data-testid="nav-clients-toggle"
                >
                  <div className="flex items-center">
                    <UserCircle className="w-5 h-5 mr-3" />
                    Clients
                  </div>
                  <ChevronDown className={cn(
                    "w-4 h-4 transition-transform",
                    clientsOpen && "rotate-180"
                  )} />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pl-4 space-y-1 mt-1">
                {clientsNavigation.map((item) => {
                  const isActive = location === item.href;
                  return (
                    <Button
                      key={item.name}
                      variant="ghost"
                      className={cn(
                        "w-full justify-start px-4 py-2 h-auto text-sm",
                        isActive
                          ? "bg-primary text-primary-foreground hover:bg-primary/90"
                          : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                      )}
                      onClick={() => {
                        setLocation(item.href);
                        onClose?.();
                      }}
                      data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <item.icon className="w-4 h-4 mr-3" />
                      {item.name}
                    </Button>
                  );
                })}
              </CollapsibleContent>
            </Collapsible>
            
            {/* Getting Work Done Section - Collapsible */}
            <Collapsible open={workOpen} onOpenChange={setWorkOpen} className="mt-2">
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  className={cn(
                    "w-full justify-between px-4 py-2 h-auto",
                    isWorkRoute
                      ? "text-primary font-medium"
                      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  )}
                  data-testid="nav-work-toggle"
                >
                  <div className="flex items-center">
                    <Briefcase className="w-5 h-5 mr-3" />
                    Getting Work Done
                  </div>
                  <ChevronDown className={cn(
                    "w-4 h-4 transition-transform",
                    workOpen && "rotate-180"
                  )} />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pl-4 space-y-1 mt-1">
                {workNavigation.map((item) => {
                  const isActive = location === item.href;
                  return (
                    <Button
                      key={item.name}
                      variant="ghost"
                      className={cn(
                        "w-full justify-start px-4 py-2 h-auto text-sm",
                        isActive
                          ? "bg-primary text-primary-foreground hover:bg-primary/90"
                          : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                      )}
                      onClick={() => {
                        setLocation(item.href);
                        onClose?.();
                      }}
                      data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <item.icon className="w-4 h-4 mr-3" />
                      {item.name}
                    </Button>
                  );
                })}
              </CollapsibleContent>
            </Collapsible>
            
            {/* Invoicing & Payments Section - Collapsible */}
            <Collapsible open={invoicingOpen} onOpenChange={setInvoicingOpen} className="mt-2">
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  className={cn(
                    "w-full justify-between px-4 py-2 h-auto",
                    isInvoicingRoute
                      ? "text-primary font-medium"
                      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  )}
                  data-testid="nav-invoicing-toggle"
                >
                  <div className="flex items-center">
                    <DollarSign className="w-5 h-5 mr-3" />
                    Invoicing & Payments
                  </div>
                  <ChevronDown className={cn(
                    "w-4 h-4 transition-transform",
                    invoicingOpen && "rotate-180"
                  )} />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pl-4 space-y-1 mt-1">
                {invoicingNavigation.map((item) => {
                  const isActive = location === item.href;
                  return (
                    <Button
                      key={item.name}
                      variant="ghost"
                      className={cn(
                        "w-full justify-start px-4 py-2 h-auto text-sm",
                        isActive
                          ? "bg-primary text-primary-foreground hover:bg-primary/90"
                          : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                      )}
                      onClick={() => {
                        setLocation(item.href);
                        onClose?.();
                      }}
                      data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <item.icon className="w-4 h-4 mr-3" />
                      {item.name}
                    </Button>
                  );
                })}
              </CollapsibleContent>
            </Collapsible>
            
            {/* Admin Section - Collapsible */}
            {user?.role === 'admin' && (
              <Collapsible open={adminOpen} onOpenChange={setAdminOpen} className="mt-4">
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    className={cn(
                      "w-full justify-between px-4 py-2 h-auto",
                      isAdminRoute
                        ? "text-primary font-medium"
                        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                    )}
                    data-testid="nav-admin-toggle"
                  >
                    <div className="flex items-center">
                      <Shield className="w-5 h-5 mr-3" />
                      Admin
                    </div>
                    <ChevronDown className={cn(
                      "w-4 h-4 transition-transform",
                      adminOpen && "rotate-180"
                    )} />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pl-4 space-y-1 mt-1">
                  {adminNavigation.map((item) => {
                    const isActive = location === item.href;
                    return (
                      <Button
                        key={item.name}
                        variant="ghost"
                        className={cn(
                          "w-full justify-start px-4 py-2 h-auto text-sm",
                          isActive
                            ? "bg-primary text-primary-foreground hover:bg-primary/90"
                            : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                        )}
                        onClick={() => {
                          setLocation(item.href);
                          onClose?.();
                        }}
                        data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <item.icon className="w-4 h-4 mr-3" />
                        {item.name}
                      </Button>
                    );
                  })}
                </CollapsibleContent>
              </Collapsible>
            )}
          </nav>
          
          {/* User Profile */}
          <div className="px-4 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center flex-1 min-w-0">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-700 truncate">
                    {user?.firstName && user?.lastName 
                      ? `${user.firstName} ${user.lastName}`
                      : user?.email || "User"
                    }
                  </p>
                  <p className="text-xs text-gray-500">
                    {user?.role ? formatRole(user.role) : "User"}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-gray-400 hover:text-gray-600 p-1"
                data-testid="button-logout"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
